import { createRoot } from 'react-dom/client'
import { useWallet, WalletProvider } from '~/lib/useWallet'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import { BrowserRouter as Router, Navigate, useRoutes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import './index.pcss'
import 'symbiosis-uikit/style.css'
import '@reach/dialog/styles.css'
import { EarnType } from '~/lib/constants'
import StakePage from '~/pages/StakePage'
import {
	earnTypeByChainId,
	earnTypeByPathName,
	pathToEarnPage,
} from '~/lib/routes'
import UnstakePage from '~/pages/UnstakePage'
import ClaimPage from '~/pages/ClaimPage'
import { EarnRoute } from '~/pages/EarnPage'
import DefaultLayout from '~/layouts/DefaultLayout'
import { CONFIG } from '~/lib/config'
import { useMatch } from 'react-router'
import TagManager from 'react-gtm-module'
import CrossChainFarmPage from '~/pages/CrossChainFarmPage'
import { VESISPage } from './pages/VESISPage'
import { CreateLockPage } from './pages/CreateLockPage'
import { ConfirmLockPage } from './pages/ConfirmLockPage'
import { LockSettingsPage } from './pages/LockSettingsPage'
import CrossChainFarmPageV2 from './pages/CrossChainFarmPageV2'
import ClaimRewardsPage from './pages/ClaimRewardsPage/RewardsPage'
// import TestPage from '~/pages/TestPage'

Sentry.init({
	dsn: CONFIG.sentry?.dsn,
	integrations: [new Integrations.BrowserTracing()],
	enabled: !!CONFIG.sentry?.dsn,
	release: APP_RELEASE,
	environment: CONFIG.environment,
	tracesSampleRate: 1.0,
})

TagManager.initialize({
	gtmId: 'GTM-WWT64HW',
})

const queryClient = new QueryClient()

const SmartRedirect: React.FC = () => {
	const wallet = useWallet()
	const match = useMatch('/lp-farm/:earnTypeName/:page')
	if (!match) {
		return (
			<Navigate
				to={pathToEarnPage(earnTypeByChainId(wallet.chainId), 'stake')}
			/>
		)
	}
	const {
		params: { earnTypeName },
	} = match
	const earnType = earnTypeByPathName(earnTypeName)
	if (earnType) {
		return <Navigate to={pathToEarnPage(earnType, 'stake')} />
	}
	return <Navigate to="/lp-farm/pancake-swap/stake" />
}

const earnRoutes = (path: string, earnType: EarnType) => {
	return [
		{
			element: <StakePage />,
			path: pathToEarnPage(earnType, 'stake'),
		},
		{
			path: pathToEarnPage(earnType, 'unstake'),
			element: <UnstakePage />,
		},
		{
			path: pathToEarnPage(earnType, 'claim'),
			element: <ClaimPage />,
		},
		{ index: true, element: <SmartRedirect /> },
	]
}

const vesisRoutes = [
	{
		element: <VESISPage />,
		index: true,
	},
	{
		path: 'create',
		element: <CreateLockPage />,
	},
	{
		path: 'confirm',
		element: <ConfirmLockPage />,
	},
	{
		path: 'settings',
		element: <LockSettingsPage />,
	},
]

function App() {
	return useRoutes([
		{
			path: '/',
			element: <DefaultLayout />,
			children: [
				{
					index: true,
					element: <Navigate to="/crosschain-farm-v2" />,
				},
				// {
				// 	path: '/test',
				// 	element: <TestPage />,
				// },
				{
					path: 'crosschain-farm',
					element: <CrossChainFarmPage />,
				},
				{
					path: 'crosschain-farm-v2',
					element: <CrossChainFarmPageV2 />,
				},
				{
					path: 'lp-farm',
					children: [
						{ index: true, element: <SmartRedirect /> },
						{
							path: 'uni-swap',
							element: <EarnRoute earnType={EarnType.uniSwap} />,
							children: earnRoutes(
								'/lp-farm/uni-swap',
								EarnType.uniSwap
							),
						},
						{
							path: 'sushi-swap',
							element: (
								<EarnRoute earnType={EarnType.sushiSwap} />
							),
							children: earnRoutes(
								'/lp-farm/sushi-swap',
								EarnType.sushiSwap
							),
						},
						{
							path: 'pancake-swap',
							element: (
								<EarnRoute earnType={EarnType.pancakeSwap} />
							),
							children: earnRoutes(
								'/lp-farm/pancake-swap',
								EarnType.pancakeSwap
							),
						},
						{ path: '*', element: <SmartRedirect /> },
					],
				},
				{
					path: 'vesis',
					children: [
						{
							element: (
								<Navigate
									to={`/vesis/${CONFIG.vesis[0].slug}`}
									replace
								/>
							),
							index: true,
						},
						...CONFIG.vesis.map((vesisConfig) => {
							return {
								path: vesisConfig.slug,
								children: vesisRoutes,
							}
						}),
					],
				},
				{
					path: 'claim-rewards',
					element: <ClaimRewardsPage />,
				},
			],
		},
		{
			path: '*',
			element: <Navigate to="/crosschain-farm-v2" replace />,
		},
	])
}

createRoot(document.getElementById('application')!).render(
	<QueryClientProvider client={queryClient}>
		<WalletProvider>
			<Router>
				<App />
			</Router>
		</WalletProvider>
	</QueryClientProvider>
)
